import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MainNavigation from '../components/MainNavigation';
import Footer from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Package, Search, Grid3X3, List, Edit, CheckCircle, 
  ExternalLink, Twitter, Instagram, Globe, Mail,
  Star, ShoppingCart, Eye, Pencil
} from 'lucide-react';
import { BACKEND_URL } from '../config';

const DesignerStorefront = () => {
  const { username } = useParams();
  const [searchParams] = useSearchParams();
  const { user, getIdToken } = useAuth();
  const navigate = useNavigate();
  
  const [creator, setCreator] = useState(null);
  const [listings, setListings] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(searchParams.get('edit') === 'true');
  const [showStorefrontSales, setShowStorefrontSales] = useState(false);
  
  // Navigation handler
  const handleNavigate = (page) => {
    if (page === 'home') navigate('/');
    else if (page === 'services') navigate('/services');
    else if (page === 'products') navigate('/products');
    else if (page === 'marketplace') navigate('/marketplace');
    else if (page === 'about') navigate('/about');
    else if (page === 'contact') navigate('/contact');
    else if (page === 'cart') navigate('/cart');
    else if (page === 'profile') navigate('/profile');
  };
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    bio: '',
    storefront_tagline: '',
    theme_color: '#0007b5',
    accent_color: '#3b82f6'
  });
  const [profilePicture, setProfilePicture] = useState(null);
  const [banner, setBanner] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Check if current user is the creator
  const isOwnProfile = user && creator && user.id === creator.user_id;

  useEffect(() => {
    fetchCreatorProfile();
  }, [username, selectedCategory, searchQuery]);

  const fetchCreatorProfile = async () => {
    try {
      let url = `${BACKEND_URL}/api/creators/${username}`;
      const params = new URLSearchParams();
      
      if (selectedCategory && selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();

        // Username was renamed — follow the redirect to the new handle.
        if (data.redirect_to && data.redirect_to !== username) {
          navigate(`/creator/${data.redirect_to}${window.location.search}`, { replace: true });
          return;
        }

        setCreator(data.creator);
        setListings(data.listings);
        setAllCategories(data.categories || []);
        setShowStorefrontSales(Boolean(data.show_storefront_sales));
        setEditForm({
          bio: data.creator.bio || '',
          storefront_tagline: data.creator.storefront_tagline || '',
          theme_color: data.creator.theme_color || '#0007b5',
          accent_color: data.creator.accent_color || '#3b82f6'
        });
      } else {
        console.error('Creator not found');
      }
    } catch (error) {
      console.error('Error fetching creator:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setUploading(true);
    try {
      const token = await getIdToken();
      
      // Upload profile picture if selected
      if (profilePicture) {
        const formData = new FormData();
        formData.append('file', profilePicture);
        
        await fetch(`${BACKEND_URL}/api/creator/upload-profile-picture`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
      }

      // Upload banner if selected
      if (banner) {
        const formData = new FormData();
        formData.append('file', banner);
        
        await fetch(`${BACKEND_URL}/api/creator/upload-banner`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
      }

      // Update profile info
      const formData = new FormData();
      formData.append('bio', editForm.bio);
      formData.append('storefront_tagline', editForm.storefront_tagline);
      formData.append('theme_color', editForm.theme_color);
      formData.append('accent_color', editForm.accent_color);

      const response = await fetch(`${BACKEND_URL}/api/creator/my-profile`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        setIsEditing(false);
        setProfilePicture(null);
        setBanner(null);
        fetchCreatorProfile();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    } finally {
      setUploading(false);
    }
  };

  const themeColor = creator?.theme_color || '#0007b5';
  const accentColor = creator?.accent_color || '#3b82f6';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading designer storefront...</p>
        </div>
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MainNavigation onNavigate={handleNavigate} currentPage="marketplace" />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Designer Not Found</h1>
            <p className="text-gray-600 mb-6">This designer profile doesn't exist or has been removed.</p>
            <Button onClick={() => navigate('/marketplace')}>
              Browse Marketplace
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <MainNavigation onNavigate={handleNavigate} currentPage="marketplace" />
      
      {/* Hero Banner Section */}
      <div 
        className="relative h-72 md:h-80"
        style={{ 
          background: creator.banner_url 
            ? 'transparent' 
            : `linear-gradient(135deg, ${themeColor} 0%, ${accentColor} 100%)`
        }}
      >
        {creator.banner_url && (
          <img 
            src={creator.banner_url.startsWith('http') ? creator.banner_url : `${BACKEND_URL}${creator.banner_url}`}
            alt="Banner" 
            className="w-full h-full object-cover"
          />
        )}
        {/* Overlay gradient for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        
        {/* Edit Banner Button (top-right — stays clear of the profile card pulled up by -mt-24) */}
        {isEditing && (
          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10">
            <label className="cursor-pointer">
              <div className="bg-white/95 backdrop-blur-sm px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg shadow-lg flex items-center gap-1.5 sm:gap-2 hover:bg-white transition">
                <Edit className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">
                  <span className="hidden sm:inline">Change Cover</span>
                  <span className="sm:hidden">Cover</span>
                </span>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setBanner(e.target.files[0])}
                className="hidden"
                data-testid="storefront-change-cover-input"
              />
            </label>
          </div>
        )}
      </div>

      {/* Profile Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative -mt-24 pb-8">
          <div className="bg-white rounded-xl shadow-xl p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Profile Picture */}
              <div className="relative flex-shrink-0 group">
                {creator.profile_picture_url ? (
                  <img 
                    src={creator.profile_picture_url.startsWith('http') ? creator.profile_picture_url : `${BACKEND_URL}${creator.profile_picture_url}`}
                    alt={creator.display_name}
                    className="w-32 h-32 md:w-40 md:h-40 rounded-2xl border-4 border-white shadow-xl object-cover"
                  />
                ) : (
                  <div 
                    className="w-32 h-32 md:w-40 md:h-40 rounded-2xl border-4 border-white shadow-xl flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${themeColor} 0%, ${accentColor} 100%)` }}
                  >
                    <span className="text-5xl font-bold text-white">
                      {creator.display_name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                {creator.is_verified && !isEditing && (
                  <div 
                    className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg"
                    style={{ backgroundColor: themeColor }}
                  >
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                )}
                {isEditing && (
                  <>
                    {/* Hover overlay effect (purely visual feedback) */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl opacity-0 group-hover:opacity-100 transition pointer-events-none">
                      <Edit className="w-8 h-8 text-white" />
                    </div>
                    {/* Persistent edit badge at bottom-right (always visible in edit mode) */}
                    <label
                      className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full flex items-center justify-center shadow-lg cursor-pointer z-20 border-2 border-white transition hover:scale-105"
                      style={{ backgroundColor: themeColor }}
                      title="Change profile picture"
                      data-testid="storefront-edit-profile-pic-badge"
                    >
                      <Pencil className="w-4 h-4 text-white" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setProfilePicture(e.target.files[0])}
                        className="hidden"
                      />
                    </label>
                  </>
                )}
              </div>

              {/* Creator Info */}
              <div className="flex-1 w-full">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900">{creator.display_name}</h1>
                    <p className="text-gray-500 mt-1">@{creator.username}</p>
                    {isEditing ? (
                      <Input
                        value={editForm.storefront_tagline}
                        onChange={(e) => setEditForm({...editForm, storefront_tagline: e.target.value})}
                        placeholder="Add a tagline (e.g., 'Professional CAD Designer')"
                        className="mt-2 max-w-md"
                      />
                    ) : (
                      creator.storefront_tagline && (
                        <p className="mt-2 text-lg" style={{ color: themeColor }}>{creator.storefront_tagline}</p>
                      )
                    )}
                  </div>
                  
                  {isOwnProfile && !isEditing && (
                    <Button 
                      onClick={() => setIsEditing(true)} 
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Edit Storefront
                    </Button>
                  )}
                  {isEditing && (
                    <div className="flex gap-2">
                      <Button onClick={handleSaveProfile} disabled={uploading} style={{ backgroundColor: themeColor }}>
                        {uploading ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button onClick={() => setIsEditing(false)} variant="outline">
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>

                {/* Bio */}
                {isEditing ? (
                  <textarea
                    value={editForm.bio}
                    onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                    placeholder="Tell visitors about yourself and your work..."
                    className="w-full p-3 border rounded-lg resize-none text-gray-700"
                    rows={3}
                  />
                ) : (
                  creator.bio && (
                    <p className="text-gray-600 leading-relaxed mb-4 max-w-2xl">{creator.bio}</p>
                  )
                )}

                {/* Theme Color Picker (Edit Mode) */}
                {isEditing && (
                  <div className="flex flex-wrap gap-4 mt-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={editForm.theme_color}
                          onChange={(e) => setEditForm({...editForm, theme_color: e.target.value})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={editForm.theme_color}
                          onChange={(e) => setEditForm({...editForm, theme_color: e.target.value})}
                          className="w-28"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Accent Color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={editForm.accent_color}
                          onChange={(e) => setEditForm({...editForm, accent_color: e.target.value})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={editForm.accent_color}
                          onChange={(e) => setEditForm({...editForm, accent_color: e.target.value})}
                          className="w-28"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Stats */}
                <div className="flex flex-wrap gap-6 mt-6">
                  <div className="text-center">
                    <span className="block text-2xl font-bold" style={{ color: themeColor }}>
                      {creator.total_listings || listings.length}
                    </span>
                    <span className="text-sm text-gray-500">Designs</span>
                  </div>
                  {showStorefrontSales && (
                    <div className="text-center" data-testid="storefront-sales-stat">
                      <span className="block text-2xl font-bold" style={{ color: themeColor }}>
                        {creator.total_sales || 0}
                      </span>
                      <span className="text-sm text-gray-500">Sales</span>
                    </div>
                  )}
                  <div className="text-center" data-testid="storefront-designer-since">
                    <span className="block text-2xl font-bold" style={{ color: themeColor }}>
                      {new Date(
                        creator.designer_approved_at ||
                        creator.member_since ||
                        creator.created_at
                      ).getFullYear()}
                    </span>
                    <span className="text-sm text-gray-500">Designer Since</span>
                  </div>
                </div>

                {/* Social Links */}
                {creator.social_links && Object.keys(creator.social_links).length > 0 && (
                  <div className="flex gap-3 mt-4">
                    {creator.social_links.twitter && (
                      <a href={creator.social_links.twitter} target="_blank" rel="noopener noreferrer" 
                         className="text-gray-400 hover:text-blue-400 transition">
                        <Twitter className="w-5 h-5" />
                      </a>
                    )}
                    {creator.social_links.instagram && (
                      <a href={creator.social_links.instagram} target="_blank" rel="noopener noreferrer"
                         className="text-gray-400 hover:text-pink-500 transition">
                        <Instagram className="w-5 h-5" />
                      </a>
                    )}
                    {creator.social_links.website && (
                      <a href={creator.social_links.website} target="_blank" rel="noopener noreferrer"
                         className="text-gray-400 hover:text-gray-600 transition">
                        <Globe className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                {/* Search */}
                <div className="relative flex-1 sm:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search designs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {/* Category Filter */}
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {allCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* View Toggle */}
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition ${viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Designs Grid/List */}
        <div className="pb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {searchQuery || selectedCategory !== 'all' 
                ? `${listings.length} Design${listings.length !== 1 ? 's' : ''} Found`
                : `All Designs (${listings.length})`
              }
            </h2>
          </div>
          
          {listings.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl shadow-sm">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No designs found</h3>
              <p className="text-gray-500">
                {searchQuery || selectedCategory !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'This designer hasn\'t published any designs yet.'
                }
              </p>
              {(searchQuery || selectedCategory !== 'all') && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {listings.map((listing) => (
                <Card 
                  key={listing.id} 
                  className="group cursor-pointer overflow-hidden hover:shadow-xl transition-all duration-300"
                  onClick={() => navigate(`/marketplace/listing/${listing.id}`)}
                  data-testid={`listing-card-${listing.id}`}
                >
                  <div className="relative aspect-square bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                    {listing.preview_images && listing.preview_images.length > 0 ? (
                      <img 
                        src={listing.preview_images[0]} 
                        alt={listing.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-16 h-16 text-gray-400" />
                      </div>
                    )}
                    {listing.has_3d_preview && (
                      <Badge 
                        className="absolute top-3 right-3 text-white text-xs"
                        style={{ backgroundColor: themeColor }}
                      >
                        3D Preview
                      </Badge>
                    )}
                    {listing.listing_type === 'assembly' && (
                      <Badge className="absolute top-3 left-3 bg-purple-600 text-white text-xs">
                        Assembly
                      </Badge>
                    )}
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <Button size="sm" className="bg-white text-gray-900 hover:bg-gray-100">
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                      {listing.title}
                    </h3>
                    <Badge variant="outline" className="text-xs mb-3">{listing.category}</Badge>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Personal</p>
                        <p className="font-bold" style={{ color: themeColor }}>${listing.personal_price}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Commercial</p>
                        <p className="font-bold" style={{ color: themeColor }}>${listing.commercial_price}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {listings.map((listing) => (
                <Card 
                  key={listing.id}
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate(`/marketplace/listing/${listing.id}`)}
                  data-testid={`listing-list-${listing.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <div className="w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                        {listing.preview_images && listing.preview_images.length > 0 ? (
                          <img 
                            src={listing.preview_images[0]} 
                            alt={listing.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-gray-900 text-lg">{listing.title}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">{listing.category}</Badge>
                              {listing.has_3d_preview && (
                                <Badge className="text-xs text-white" style={{ backgroundColor: themeColor }}>3D</Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">From</p>
                            <p className="text-xl font-bold" style={{ color: themeColor }}>${listing.personal_price}</p>
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm mt-2 line-clamp-2">{listing.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DesignerStorefront;
